import React, { useState } from "react";
import "react-datepicker/dist/react-datepicker-min.module.css";
import { useTranslation } from "react-i18next";
import "reactjs-popup/dist/index.css";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import { Spinner } from "spot-component-library/lib/components/basic/spinner/Spinner";
import { SPOTNotification } from "spot-component-library/lib/components/notification/SPOTNotification";

import { Etracker, setEtPageName } from "spot-component-library/lib/components/etracker/Etracker";
import FormatNumber from "../../components/FormatNumber";
import ToolTip from "../../components/ToolTip/ToolTip";
import debtService from "../../services/DebtService";
import rateAgreementService from "../../services/RateAgreementService";
import utilService from "../../services/UtilService";
import { Balance } from "../../types/Balance";
import { PaymentFormData } from "../../types/PaymentFormData";
import { RateAgreementOptions } from "../../types/RateAgreementOptions";
import { RateData } from "../../types/RateData";
import { calculateCompleteBalance, calculateRemainingBalance, readLanguageFromCookie } from "../../utils/helper";
import { PaymentForm } from "../payment/PaymentForm";
import { RateCalculation } from "../rateCalculation/RateCalculation";

import { useSettlementFee } from "../../contexts/SettlementFeeContext";
import { useCurrency, useTenant } from "../../contexts/TenantContext";
import { RatePlanSummary } from "../ratePlanSummary/RatePlanSummary";
import { SuccessPage } from "../success/SuccessPage";
import "./RateAgreementMain.scss";

export const RateAgreementMain = () => {
    const { t } = useTranslation();
    const { tenant } = useTenant();
    const currency = useCurrency();
    const settlementFee = useSettlementFee();
    const [campaign, setCampaign] = React.useState<string | null>("");
    const [activePage, setActivePage] = React.useState<string>("");
    const [rateData, setRateData] = React.useState<RateData>({});
    const [paymentForm, setPaymentForm] = useState<PaymentFormData>(new PaymentFormData());
    const [rateAgreementOptions, setRateAgreementOptions] = useState<RateAgreementOptions>({} as RateAgreementOptions);
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [debtBalance, setDebtBalance] = React.useState<Balance>(new Balance(0.0));
    const [isPageLoaded, setIsPageLoaded] = React.useState<boolean>(false);
    const [isRateAgreementSubmitting, setIsRateAgreementSubmitting] = React.useState<boolean>(false);
    const [remainingBalance, setRemainingBalance] = React.useState<number | undefined>(
        calculateRemainingBalance(rateData.rateAmount, rateData.rateNumber, calculateCompleteBalance(debtBalance, settlementFee.value))
    );

    React.useEffect(() => {
        initData().then(() => setIsPageLoaded(true));
    }, []);

    async function initData() {
        try {
            const debtNumberResponse = await utilService.getDebtNo();
            const urlSearchParams = window.location.search;

            if (urlSearchParams !== "") {
                const urlParams = new URLSearchParams(urlSearchParams);
                setCampaign(urlParams.get("cmp"));
            }

            const [balance, rateAgreementOptions] = await Promise.all([
                debtService.getBalance(debtNumberResponse),
                rateAgreementService.getRateAgreementOptions(debtNumberResponse),
            ]);

            setDebtBalance(balance);

            setRateData({
                ...rateData,
                debtNumber: debtNumberResponse,
                rateAmount: rateAgreementOptions.initialRate,
                rateNumber: rateAgreementOptions.minNumberOfRates,
            });

            setRateAgreementOptions(rateAgreementOptions);
            setActivePage("calculation");
        } catch (error) {
            setActivePage("error");
            switch ((error as Error).message) {
                case "404":
                    setErrorMessage(t("APP_ROOT.ERROR.MESSAGE_404"));
                    break;
                case "422":
                    setErrorMessage(t("APP_ROOT.ERROR.MESSAGE_422"));
                    break;
                default: {
                    setErrorMessage(t("APP_ROOT.ERROR.SERVER_MESSAGE_DEFAULT"));
                    break;
                }
            }
        }
    }

    function toChangeRemainingBalance(newBalance: number) {
        setRemainingBalance(newBalance);
    }

    function toCalculation(paymentForm: PaymentFormData) {
        setActivePage("calculation");
        if (paymentForm) {
            setPaymentForm(paymentForm);
        }
    }

    function toPayment(rateData: RateData | undefined) {
        setActivePage("payment");
        if (rateData) {
            setRateData(rateData);
        }
    }

    function toRatePlanSummary(paymentForm: PaymentFormData) {
        setActivePage("summary");
        setPaymentForm(paymentForm);
    }

    async function submitData() {
        setIsRateAgreementSubmitting(true);

        try {
            const isAvailable = await rateAgreementService.isRateAgreementAvailable(rateData.debtNumber as string);
            if (isAvailable) {
                rateData.language = readLanguageFromCookie();
                const confirmation = await rateAgreementService.createRateAgreement(rateData, paymentForm, campaign, rateAgreementOptions);
                if (confirmation.payGateUrl) {
                    console.log(confirmation);
                    console.log(confirmation.payGateUrl);
                    window.location.href = confirmation.payGateUrl;
                } else {
                    setActivePage("success");
                }
            } else {
                setIsRateAgreementSubmitting(false);
                setErrorMessage(t("APP_ROOT.ERROR.MESSAGE_422"));
            }
        } catch (error) {
            setIsRateAgreementSubmitting(false);
            switch ((error as Error).message) {
                case "404":
                    setErrorMessage(t("APP_ROOT.ERROR.MESSAGE_404"));
                    break;
                case "422":
                    setErrorMessage(t("APP_ROOT.ERROR.MESSAGE_422"));
                    break;
                default: {
                    setErrorMessage(t("APP_ROOT.ERROR.SERVER_MESSAGE_DEFAULT"));
                    break;
                }
            }
        }
    }

    return (
        <div className="rate-agreement-container" data-test="rateAgreementMain">
            {tenant?.name && <Etracker pageName={"Ratenvereinbarung"} popto={"1500"} tenant={tenant.name} language={readLanguageFromCookie()} />}

            <div className="rate-agreement-wrapper">
                {isPageLoaded && (
                    <>
                        {activePage !== "success" && <h2 className={"rate-agreement-title"}>{t("APP_ROOT.RATE_AGREEMENT_OVERVIEW.Einleitungstext")}</h2>}

                        {(activePage === "calculation" || activePage === "payment" || activePage === "summary") && (
                            <div data-test="paymentBox" id={"paymentBox"}>
                                <Panel>
                                    <div className={"whiteBlock"}>
                                        <div className={"row-wrapper"}>
                                            <div>{t("APP_ROOT.RATE_AGREEMENT_OVERVIEW.Forderungsnummer")}</div>
                                            <div data-test="debtNumber">
                                                <b>{rateData.debtNumber}</b>
                                            </div>
                                        </div>
                                        <div className={"row-wrapper"}>
                                            <div>{t("APP_ROOT.RATE_AGREEMENT_OVERVIEW.OffeneForderungssumme")}</div>
                                            <div data-test="openDebt">
                                                <FormatNumber number={debtBalance.balance} currency={currency} />
                                            </div>
                                        </div>
                                        <div className={"row-wrapper"}>
                                            <div className={"text-middle"}>
                                                {t("APP_ROOT.RATE_AGREEMENT_OVERVIEW.Einigungsgebühr")}
                                                <ToolTip text={t("APP_ROOT.RATE_AGREEMENT_OVERVIEW.SETTLEMENT_FEE_TOOLTIP")} />
                                            </div>
                                            <div data-test="openSettlementFee">
                                                {settlementFee.requestIsPending ? (
                                                    <Spinner isLoading size={20} />
                                                ) : (
                                                    <FormatNumber number={settlementFee.value} currency={currency} />
                                                )}
                                            </div>
                                        </div>
                                        <div className={"row-wrapper"}>
                                            <div>
                                                <b>{t("APP_ROOT.RATE_AGREEMENT_OVERVIEW.Summe")}</b>
                                            </div>
                                            <div data-test="sum">
                                                <b>
                                                    <FormatNumber number={calculateCompleteBalance(debtBalance, settlementFee.value)} currency={currency} />
                                                </b>
                                            </div>
                                        </div>
                                    </div>
                                </Panel>
                            </div>
                        )}

                        {errorMessage && activePage === "error" && <SPOTNotification text={errorMessage as string} active={true} type={"error"} />}

                        {activePage === "calculation" && (
                            <div>
                                {setEtPageName("RateAgreementRatePlan")}
                                <RateCalculation
                                    rateData={rateData}
                                    debtBalance={debtBalance}
                                    rateAgreementOptions={rateAgreementOptions}
                                    onNext={toPayment}
                                    stateChanger={toChangeRemainingBalance}
                                />
                            </div>
                        )}

                        {activePage === "payment" && (
                            <div>
                                {setEtPageName("RateAgreementPaymentType")}
                                <PaymentForm paymentForm={paymentForm} rateData={rateData as Required<RateData>} onBack={toCalculation} onNext={toRatePlanSummary} />
                            </div>
                        )}

                        {activePage === "summary" && (
                            <div>
                                {setEtPageName("RateAgreementSummary")}
                                <RatePlanSummary
                                    errorMessage={errorMessage}
                                    debtBalance={debtBalance.balance}
                                    rateAgreementOptions={rateAgreementOptions}
                                    paymentForm={paymentForm}
                                    rateData={rateData}
                                    onBack={() => toPayment(undefined)}
                                    showSpinner={isRateAgreementSubmitting}
                                    onNext={submitData}
                                    debtNumber={rateData.debtNumber}
                                />
                            </div>
                        )}

                        {activePage === "success" && (
                            <div>
                                {setEtPageName("RateAgreementSuccess")}
                                <SuccessPage paymentFormData={paymentForm} rateData={rateData} remainingBalance={remainingBalance} />
                            </div>
                        )}
                    </>
                )}

                <Spinner isLoading={!isPageLoaded} type="page" color={"grey"} />
            </div>
        </div>
    );
};

export default RateAgreementMain;
